import { t } from './i18n.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Mail,
  Pencil,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
  Undo2,
  Upload,
  UserPlus,
  UserRound,
  Users,
} from 'lucide-react';
import { api, send } from './api.js';
import { IconButton, Loading, Modal, Notice, displayDate } from './components.js';

export interface Contact {
  id: string;
  name: string;
  email: string;
  otherEmails?: string[];
  phone: string;
  company: string;
  notes: string;
  starred?: boolean;
  labels?: string[];
  deletedAt?: string | null;
  updatedAt: string;
}
interface Label {
  id: string;
  name: string;
  count: number;
}
interface Correspondent {
  email: string;
  name: string;
  lastReceivedAt: string;
  count: number;
}
interface DuplicateGroup {
  key: string;
  name: string;
  contacts: Contact[];
}
type Tab = 'book' | 'frequent' | 'others' | 'duplicates' | 'bin';
type SortKey = 'name' | 'email' | 'company';
const empty = { id: '', name: '', email: '', others: '', phone: '', company: '', notes: '' };
// The form edits secondary addresses as free text, one per line; the server normalises them.
const splitAddresses = (value: string) =>
  value
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter(Boolean);

export function Contacts({ onCompose }: { onCompose: (email: string) => void }) {
  const [query, setQuery] = useState(''),
    [rows, setRows] = useState<Contact[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [othersError, setOthersError] = useState(''),
    [notice, setNotice] = useState(''),
    [draft, setDraft] = useState<typeof empty | undefined>(),
    [saving, setSaving] = useState(false),
    [importing, setImporting] = useState(false),
    [confirm, setConfirm] = useState<Contact | undefined>(),
    [tab, setTab] = useState<Tab>('book'),
    [sort, setSort] = useState<SortKey>('name'),
    [selected, setSelected] = useState<Set<string>>(new Set()),
    [bulkConfirm, setBulkConfirm] = useState(false),
    [correspondents, setCorrespondents] = useState<Correspondent[]>(),
    [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]),
    [ignored, setIgnored] = useState<Set<string>>(new Set()),
    [keep, setKeep] = useState<Record<string, string>>({}),
    [labels, setLabels] = useState<Label[]>([]),
    [labelFilter, setLabelFilter] = useState(''),
    [labelMenu, setLabelMenu] = useState(false),
    [manageLabels, setManageLabels] = useState(false),
    [newLabel, setNewLabel] = useState(''),
    [labelError, setLabelError] = useState(''),
    [labelBusy, setLabelBusy] = useState(false),
    [binRows, setBinRows] = useState<Contact[]>(),
    [purgeTarget, setPurgeTarget] = useState<Contact>(),
    [purgeError, setPurgeError] = useState(''),
    [purging, setPurging] = useState(false),
    [purgeConfirm, setPurgeConfirm] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const othersInput = useRef<HTMLTextAreaElement>(null);
  const purgeCancel = useRef<HTMLButtonElement>(null);
  useEffect(() => setOthersError(''), [draft?.id]);
  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setNotice('');
    if (file.size > 4_000_000) {
      setError(t('Le fichier dépasse 4 Mo : découpez votre carnet avant l’import.'));
      return;
    }
    setImporting(true);
    try {
      const report = await send<{
        format: string;
        total: number;
        imported: number;
        skippedExisting: number;
        skippedDuplicate: number;
        invalid: string[];
        extraEmails: number;
        truncated: boolean;
        labelsCreated: number;
        labelsSkipped: string[];
      }>('/contacts/import', { content: await file.text(), filename: file.name });
      const parts = [
        t(report.imported > 1 ? '{n} contacts importés' : '{n} contact importé', {
          n: report.imported,
        }),
      ];
      if (report.skippedExisting)
        parts.push(t('{n} déjà dans votre carnet', { n: report.skippedExisting }));
      if (report.skippedDuplicate)
        parts.push(t('{n} en double dans le fichier', { n: report.skippedDuplicate }));
      if (report.invalid.length)
        parts.push(t('{n} ligne(s) sans adresse valide', { n: report.invalid.length }));
      if (report.labelsCreated)
        parts.push(t('{n} libellé(s) créé(s) depuis les groupes', { n: report.labelsCreated }));
      if (report.labelsSkipped?.length)
        parts.push(
          t('{n} groupe(s) non repris : limite de libellés atteinte', {
            n: report.labelsSkipped.length,
          }),
        );
      if (report.extraEmails)
        parts.push(t('{n} adresse(s) secondaire(s) non reprise(s)', { n: report.extraEmails }));
      if (report.truncated) parts.push(t('limite de 5 000 contacts atteinte'));
      setNotice(
        t('Import {format} terminé : {summary}.', {
          format: report.format === 'vcard' ? 'vCard' : 'CSV',
          summary: parts.join(', '),
        }),
      );
      await load(query);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const load = useCallback(
    (q: string, label = labelFilter) => {
      setLoading(true);
      return api<Contact[]>(
        '/contacts?q=' +
          encodeURIComponent(q) +
          (label ? '&label=' + encodeURIComponent(label) : ''),
      )
        .then((r) => {
          setRows(r);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [labelFilter],
  );
  useEffect(() => {
    const timer = setTimeout(() => void load(query), 200);
    return () => clearTimeout(timer);
  }, [query, load]);

  const save = async () => {
    if (!draft) return;
    const lines = draft.others.split(/\r?\n/);
    const invalidLine = lines.findIndex((line) =>
      line
        .split(/[\s,;]+/)
        .filter(Boolean)
        .some((address) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)),
    );
    if (invalidLine >= 0) {
      setOthersError(
        t('Adresse secondaire invalide à la ligne {n}. Utilisez le format nom@domaine.ci.', {
          n: invalidLine + 1,
        }),
      );
      othersInput.current?.focus();
      return;
    }
    if (splitAddresses(draft.others).length > 10) {
      setOthersError(t('Ajoutez au maximum dix adresses secondaires.'));
      othersInput.current?.focus();
      return;
    }
    setOthersError('');
    // A list refresh may finish while the form request is running. Keep form errors owned by the
    // form request so a successful background refresh cannot hide a duplicate-address response.
    setError('');
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        email: draft.email,
        otherEmails: splitAddresses(draft.others),
        phone: draft.phone,
        company: draft.company,
        notes: draft.notes,
      };
      await send('/contacts' + (draft.id ? '/' + draft.id : ''), body, draft.id ? 'PATCH' : 'POST');
      setDraft(undefined);
      setNotice(draft.id ? t('Contact mis à jour.') : t('Contact ajouté à votre carnet.'));
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (c: Contact) => {
    try {
      await api('/contacts/' + c.id, { method: 'DELETE' });
      setConfirm(undefined);
      setNotice(t('Contact déplacé dans la corbeille.'));
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const loadExtras = useCallback(async () => {
    const [c, d, l, b] = await Promise.all([
      api<Correspondent[]>('/contacts/correspondents'),
      api<DuplicateGroup[]>('/contacts/duplicates'),
      api<Label[]>('/contacts/labels'),
      api<Contact[]>('/contacts/bin'),
    ]);
    setCorrespondents(c);
    setDuplicates(d);
    setLabels(l);
    setBinRows(b);
  }, []);
  useEffect(() => {
    void loadExtras().catch((e) => setError(e.message));
  }, [loadExtras]);
  const refreshAll = async () => {
    await load(query);
    await loadExtras().catch((e) => setError(e.message));
  };
  const toggleStar = async (c: Contact) => {
    try {
      await send(`/contacts/${c.id}/star`, { starred: !c.starred });
      setRows((list) => list.map((x) => (x.id === c.id ? { ...x, starred: !c.starred } : x)));
    } catch (e: any) {
      setError(e.message);
    }
  };
  const bulkDelete = async () => {
    try {
      const r = await send<{ deleted: number }>('/contacts/bulk-delete', { ids: [...selected] });
      setBulkConfirm(false);
      setSelected(new Set());
      setNotice(
        t(
          r.deleted > 1
            ? '{n} contacts déplacés dans la corbeille.'
            : '{n} contact déplacé dans la corbeille.',
          { n: r.deleted },
        ),
      );
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const mergeGroup = async (g: DuplicateGroup) => {
    const keepId = keep[g.key] ?? g.contacts[0].id;
    try {
      await send('/contacts/merge', {
        keepId,
        mergeIds: g.contacts.filter((c) => c.id !== keepId).map((c) => c.id),
      });
      setNotice(t('Doublons fusionnés.'));
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const labelName = (id: string) => labels.find((l) => l.id === id)?.name ?? '';
  const applyLabel = async (label: Label, on: boolean) => {
    try {
      await send(`/contacts/labels/${label.id}/members`, {
        [on ? 'add' : 'remove']: [...selected],
      });
      setLabelMenu(false);
      setNotice(
        on
          ? t('Libellé « {name} » appliqué.', { name: label.name })
          : t('Libellé « {name} » retiré.', { name: label.name }),
      );
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const createLabel = async () => {
    if (!newLabel.trim() || labelBusy) return;
    setLabelError('');
    setLabelBusy(true);
    try {
      const l = await send<Label>('/contacts/labels', { name: newLabel.trim() });
      setNewLabel('');
      setLabels((list) => [...list, l].sort((a, b) => a.name.localeCompare(b.name)));
      setNotice(t('Libellé créé.'));
    } catch (e: any) {
      setLabelError(e.message);
    } finally {
      setLabelBusy(false);
    }
  };
  const deleteLabel = async (l: Label) => {
    setLabelError('');
    try {
      await api(`/contacts/labels/${l.id}`, { method: 'DELETE' });
      if (labelFilter === l.id) setLabelFilter('');
      setNotice(t('Libellé supprimé.'));
      await refreshAll();
    } catch (e: any) {
      setLabelError(e.message);
    }
  };
  const restoreContact = async (c: Contact) => {
    try {
      await send(`/contacts/${c.id}/restore`, {});
      setNotice(t('Contact restauré.'));
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const purgeContact = async (c: Contact) => {
    if (purging) return;
    setPurging(true);
    setPurgeError('');
    try {
      await api(`/contacts/${c.id}?permanent=true`, { method: 'DELETE' });
      setPurgeTarget(undefined);
      setNotice(t('Contact supprimé définitivement.'));
      await refreshAll();
    } catch (e: any) {
      setPurgeError(e.message);
    } finally {
      setPurging(false);
    }
  };
  const emptyBin = async () => {
    try {
      const r = await send<{ deleted: number }>('/contacts/bin/purge', {});
      setPurgeConfirm(false);
      setNotice(
        t('Corbeille vidée : {n} contact(s) supprimé(s) définitivement.', { n: r.deleted }),
      );
      await refreshAll();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const sorted = [...rows].sort((a, b) =>
    (a[sort] || a.email).localeCompare(b[sort] || b.email, undefined, { sensitivity: 'base' }),
  );
  const starredRows = sorted.filter((c) => c.starred),
    plainRows = sorted.filter((c) => !c.starred);
  const visibleDuplicates = duplicates.filter((g) => !ignored.has(g.key));
  const frequent = (correspondents ?? [])
    .filter((c) => c.count > 1)
    .sort((a, b) => b.count - a.count);
  const allSelected = rows.length > 0 && rows.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((c) => c.id)));
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const row = (c: Contact) => (
    <tr key={c.id} className={selected.has(c.id) ? 'selected' : ''}>
      <td className="contact-col-check">
        <input
          type="checkbox"
          checked={selected.has(c.id)}
          onChange={() => toggleOne(c.id)}
          aria-label={t('Sélectionner {name}', { name: c.name || c.email })}
        />
      </td>
      <td className="contact-col-name">
        <div className="contact-name-wrap">
          <span className="contact-initial" aria-hidden="true">
            {(c.name || c.email)[0].toUpperCase()}
          </span>
          <span className="contact-name-cell">
            <strong>{c.name || c.email}</strong>
            <small className="contact-mobile-email">
              {[c.email, ...(c.otherEmails ?? [])].join(', ')}
            </small>
            {c.notes && <small className="contact-notes">{c.notes}</small>}
            {!!c.labels?.length && (
              <span className="contact-label-chips">
                {c.labels.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="contact-chip"
                    onClick={() => setLabelFilter(id)}
                    title={t('Filtrer par ce libellé')}
                  >
                    <Tag size={11} /> {labelName(id)}
                  </button>
                ))}
              </span>
            )}
          </span>
        </div>
      </td>
      <td className="contact-col-email">
        {c.email}
        {Boolean(c.otherEmails?.length) && (
          <small className="contact-other-emails">{c.otherEmails!.join(', ')}</small>
        )}
      </td>
      <td className="contact-col-phone">{c.phone}</td>
      <td className="contact-col-company">{c.company}</td>
      <td className="contact-col-actions">
        <span className="contact-tools">
          <IconButton
            label={c.starred ? t('Retirer des favoris') : t('Ajouter aux favoris')}
            active={Boolean(c.starred)}
            onClick={() => void toggleStar(c)}
          >
            <Star size={17} className={c.starred ? 'star-on' : ''} />
          </IconButton>
          <IconButton
            label={t('Écrire à {email}', { email: c.email })}
            onClick={() => onCompose(c.email)}
          >
            <Mail size={17} />
          </IconButton>
          <IconButton
            label={t('Modifier {name}', { name: c.name || c.email })}
            onClick={() => {
              setError('');
              setDraft({ ...empty, ...c, others: (c.otherEmails ?? []).join('\n') });
            }}
          >
            <Pencil size={17} />
          </IconButton>
          <IconButton
            label={t('Supprimer {name}', { name: c.name || c.email })}
            onClick={() => setConfirm(c)}
          >
            <Trash2 size={17} />
          </IconButton>
        </span>
      </td>
    </tr>
  );
  const sortHeader = (key: SortKey, label: string, className?: string) => (
    <th className={className} aria-sort={sort === key ? 'ascending' : 'none'}>
      <button className="contact-sort" onClick={() => setSort(key)}>
        {label}
      </button>
    </th>
  );
  const correspondentTable = (list: Correspondent[], emptyText: string) =>
    !correspondents ? (
      <Loading label={t('Chargement des correspondants…')} />
    ) : list.length === 0 ? (
      <p className="contacts-empty">{emptyText}</p>
    ) : (
      <div className="contact-table-wrap">
        <table className="contact-table">
          <thead>
            <tr>
              <th>{t('Nom')}</th>
              <th className="contact-col-email">{t('E-mail')}</th>
              <th>{t('Dernier message')}</th>
              <th>{t('Messages reçus')}</th>
              <th className="contact-col-actions">
                <span className="sr-only">{t('Actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.email}>
                <td className="contact-col-name">
                  <div className="contact-name-wrap">
                    <span className="contact-initial" aria-hidden="true">
                      {(c.name || c.email)[0].toUpperCase()}
                    </span>
                    <span className="contact-name-cell">
                      <strong>{c.name || c.email}</strong>
                      <small className="contact-mobile-email">{c.email}</small>
                    </span>
                  </div>
                </td>
                <td className="contact-col-email">{c.email}</td>
                <td>{displayDate(c.lastReceivedAt)}</td>
                <td>{c.count}</td>
                <td className="contact-col-actions">
                  <span className="contact-tools">
                    <IconButton
                      label={t('Ajouter {email} au carnet', { email: c.email })}
                      onClick={() => {
                        setError('');
                        setDraft({ ...empty, name: c.name, email: c.email });
                      }}
                    >
                      <UserPlus size={17} />
                    </IconButton>
                    <IconButton
                      label={t('Écrire à {email}', { email: c.email })}
                      onClick={() => onCompose(c.email)}
                    >
                      <Mail size={17} />
                    </IconButton>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <section className="contacts" aria-label={t('Carnet d’adresses')}>
      <header className="contacts-head">
        <h1>
          <UserRound size={20} /> {t('Carnet d’adresses')}
          {!loading && !query && <span className="contact-count">{rows.length}</span>}
        </h1>
        <div className="contacts-actions">
          <div className="contacts-search">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              aria-label={t('Rechercher un contact')}
              placeholder={t('Nom, adresse ou société')}
              value={query}
              maxLength={120}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <input
            ref={fileInput}
            type="file"
            hidden
            accept=".vcf,.csv,text/vcard,text/x-vcard,text/csv"
            data-testid="contacts-import-input"
            onChange={(e) => void importFile(e.target.files?.[0])}
          />
          <button
            className="text-button"
            onClick={() => fileInput.current?.click()}
            disabled={importing}
          >
            <Upload size={16} /> {importing ? t('Import en cours…') : t('Importer (vCard, CSV)')}
          </button>
          <a className="text-button" href="/v1/contacts/export" download>
            <Download size={16} /> {t('Exporter (vCard)')}
          </a>
          <button
            className="primary"
            onClick={() => {
              setError('');
              setDraft({ ...empty });
            }}
          >
            <Plus size={16} /> {t('Nouveau contact')}
          </button>
        </div>
      </header>
      <nav className="contact-tabs" aria-label={t('Sections du carnet')}>
        {(
          [
            ['book', t('Contacts'), rows.length],
            ['frequent', t('Fréquents'), frequent.length],
            ['others', t('Autres contacts'), correspondents?.length ?? 0],
            ['duplicates', t('Doublons'), visibleDuplicates.length],
            ['bin', t('Corbeille'), binRows?.length ?? 0],
          ] as [Tab, string, number][]
        ).map(([id, label, count]) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
            {label}
            {count > 0 && <span className="folder-count">{count}</span>}
          </button>
        ))}
      </nav>
      {tab === 'book' && (
        <div className="contact-labels-row" role="group" aria-label={t('Libellés')}>
          <button
            type="button"
            className={`contact-chip ${labelFilter === '' ? 'active' : ''}`}
            onClick={() => setLabelFilter('')}
          >
            {t('Tous')}
          </button>
          {labels.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`contact-chip ${labelFilter === l.id ? 'active' : ''}`}
              onClick={() => setLabelFilter(labelFilter === l.id ? '' : l.id)}
            >
              <Tag size={12} /> {l.name}
              <span className="contact-chip-count">{l.count}</span>
            </button>
          ))}
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setLabelError('');
              setManageLabels(true);
            }}
          >
            {t('Gérer les libellés')}
          </button>
          {labelFilter && (
            <button
              type="button"
              className="text-button"
              onClick={() => onCompose(rows.map((c) => c.email).join(', '))}
              disabled={!rows.length}
            >
              <Mail size={14} /> {t('Écrire au libellé')}
            </button>
          )}
        </div>
      )}
      {error && !draft && <Notice type="error">{error}</Notice>}
      {notice && !error && <Notice type="success">{notice}</Notice>}
      {tab === 'book' && visibleDuplicates.length > 0 && (
        <div className="contact-duplicates-banner" role="status">
          <Users size={16} />
          <span>
            {t(
              visibleDuplicates.length > 1
                ? '{n} groupes de doublons trouvés : {names}'
                : '{n} groupe de doublons trouvé : {names}',
              {
                n: visibleDuplicates.length,
                names: visibleDuplicates
                  .slice(0, 3)
                  .map((g) => g.name)
                  .join(', '),
              },
            )}
          </span>
          <button
            className="text-button"
            onClick={() => setIgnored(new Set(duplicates.map((g) => g.key)))}
          >
            {t('Ignorer')}
          </button>
          <button className="text-button" onClick={() => setTab('duplicates')}>
            {t('Afficher')}
          </button>
        </div>
      )}
      {tab === 'book' && selected.size > 0 && (
        <div className="contact-bulk" role="status">
          <strong>
            {t(selected.size > 1 ? '{n} sélectionnés' : '{n} sélectionné', { n: selected.size })}
          </strong>
          <button
            className="text-button"
            onClick={() =>
              onCompose(
                rows
                  .filter((c) => selected.has(c.id))
                  .map((c) => c.email)
                  .join(', '),
              )
            }
          >
            <Mail size={15} /> {t('Écrire')}
          </button>
          <span className="contact-label-menu">
            <button
              className="text-button"
              onClick={() => setLabelMenu(!labelMenu)}
              aria-expanded={labelMenu}
            >
              <Tag size={15} /> {t('Libellé')}
            </button>
            {labelMenu && (
              <div className="contact-label-popover" role="menu">
                {labels.length === 0 && (
                  <p className="muted">
                    {t('Aucun libellé. Créez-en un depuis « Gérer les libellés ».')}
                  </p>
                )}
                {labels.map((l) => (
                  <div key={l.id} className="contact-label-option" role="menuitem">
                    <span>{l.name}</span>
                    <button className="text-button" onClick={() => void applyLabel(l, true)}>
                      {t('Appliquer')}
                    </button>
                    <button className="text-button" onClick={() => void applyLabel(l, false)}>
                      {t('Retirer')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </span>
          <button className="text-button" onClick={() => setBulkConfirm(true)}>
            <Trash2 size={15} /> {t('Supprimer')}
          </button>
          <button className="text-button" onClick={() => setSelected(new Set())}>
            {t('Annuler la sélection')}
          </button>
        </div>
      )}
      {tab === 'frequent' ? (
        correspondentTable(frequent, t('Aucun correspondant fréquent pour le moment.'))
      ) : tab === 'others' ? (
        correspondentTable(
          correspondents ?? [],
          t('Toutes les personnes qui vous ont écrit figurent déjà dans votre carnet.'),
        )
      ) : tab === 'bin' ? (
        !binRows ? (
          <Loading label={t('Chargement de la corbeille…')} />
        ) : binRows.length === 0 ? (
          <p className="contacts-empty">
            {t('La corbeille est vide. Les contacts supprimés y restent trente jours.')}
          </p>
        ) : (
          <>
            <div className="contact-bulk" role="status">
              <span>
                {t(
                  'Les contacts supprimés sont conservés trente jours puis retirés définitivement.',
                )}
              </span>
              <button className="text-button" onClick={() => setPurgeConfirm(true)}>
                <Trash2 size={15} /> {t('Vider la corbeille')}
              </button>
            </div>
            <div className="contact-table-wrap">
              <table className="contact-table">
                <thead>
                  <tr>
                    <th>{t('Nom')}</th>
                    <th className="contact-col-email">{t('E-mail')}</th>
                    <th>{t('Supprimé le')}</th>
                    <th className="contact-col-actions">
                      <span className="sr-only">{t('Actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {binRows.map((c) => (
                    <tr key={c.id}>
                      <td className="contact-col-name">
                        <div className="contact-name-wrap">
                          <span className="contact-initial" aria-hidden="true">
                            {(c.name || c.email)[0].toUpperCase()}
                          </span>
                          <span className="contact-name-cell">
                            <strong>{c.name || c.email}</strong>
                            <small className="contact-mobile-email">{c.email}</small>
                          </span>
                        </div>
                      </td>
                      <td className="contact-col-email">{c.email}</td>
                      <td>{c.deletedAt ? displayDate(c.deletedAt, true) : ''}</td>
                      <td className="contact-col-actions">
                        <span className="contact-tools">
                          <IconButton
                            label={t('Restaurer {name}', { name: c.name || c.email })}
                            onClick={() => void restoreContact(c)}
                          >
                            <Undo2 size={17} />
                          </IconButton>
                          <IconButton
                            label={t('Supprimer définitivement {name}', {
                              name: c.name || c.email,
                            })}
                            onClick={() => {
                              setPurgeError('');
                              setPurgeTarget(c);
                            }}
                          >
                            <Trash2 size={17} />
                          </IconButton>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : tab === 'duplicates' ? (
        visibleDuplicates.length === 0 ? (
          <p className="contacts-empty">{t('Aucun doublon détecté.')}</p>
        ) : (
          <div className="duplicate-groups">
            {visibleDuplicates.map((g) => (
              <section className="duplicate-group" key={g.key} aria-label={g.name}>
                <h2>{g.name}</h2>
                <p className="muted">
                  {t(
                    'Choisissez la fiche à conserver ; les champs vides sont complétés depuis les autres, qui sont ensuite supprimées.',
                  )}
                </p>
                {g.contacts.map((c) => (
                  <label key={c.id} className="duplicate-choice">
                    <input
                      type="radio"
                      name={`keep-${g.key}`}
                      checked={(keep[g.key] ?? g.contacts[0].id) === c.id}
                      onChange={() => setKeep({ ...keep, [g.key]: c.id })}
                    />
                    <span>
                      <strong>{c.email}</strong>
                      <small>
                        {[c.company, c.phone, c.notes].filter(Boolean).join(' · ') ||
                          t('aucun autre détail')}
                      </small>
                    </span>
                  </label>
                ))}
                <div className="modal-actions">
                  <button
                    className="text-button"
                    onClick={() => setIgnored(new Set([...ignored, g.key]))}
                  >
                    {t('Ignorer')}
                  </button>
                  <button className="primary" onClick={() => void mergeGroup(g)}>
                    {t('Fusionner')}
                  </button>
                </div>
              </section>
            ))}
          </div>
        )
      ) : loading ? (
        <Loading label={t('Chargement du carnet…')} />
      ) : rows.length === 0 ? (
        <p className="contacts-empty">
          {query
            ? t('Aucun contact ne correspond à cette recherche.')
            : t(
                'Votre carnet est vide. Les contacts que vous ajoutez restent privés et vous sont proposés lors de la rédaction.',
              )}
        </p>
      ) : (
        <div className="contact-table-wrap">
          <table className="contact-table">
            <thead>
              <tr>
                <th className="contact-col-check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label={t('Sélectionner tous les contacts affichés')}
                  />
                </th>
                {sortHeader('name', t('Nom'))}
                {sortHeader('email', t('E-mail'), 'contact-col-email')}
                <th className="contact-col-phone">{t('Téléphone')}</th>
                {sortHeader('company', t('Société'), 'contact-col-company')}
                <th className="contact-col-actions">
                  <span className="sr-only">{t('Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {starredRows.length > 0 && (
                <tr className="contact-group">
                  <td colSpan={6}>
                    <Star size={14} /> {t('Favoris ({n})', { n: starredRows.length })}
                  </td>
                </tr>
              )}
              {starredRows.map(row)}
              {starredRows.length > 0 && plainRows.length > 0 && (
                <tr className="contact-group">
                  <td colSpan={6}>{t('Tous les contacts ({n})', { n: plainRows.length })}</td>
                </tr>
              )}
              {plainRows.map(row)}
            </tbody>
          </table>
        </div>
      )}
      {manageLabels && (
        <Modal title={t('Gérer les libellés')} onClose={() => setManageLabels(false)}>
          <div className="stack-form label-manager">
            <label htmlFor="contact-label-name">{t('Nom du libellé')}</label>
            <form
              className="label-create"
              onSubmit={(e) => {
                e.preventDefault();
                void createLabel();
              }}
            >
              <input
                id="contact-label-name"
                disabled={labelBusy}
                placeholder={t('Clients, Fournisseurs, Équipe…')}
                maxLength={60}
                value={newLabel}
                aria-invalid={Boolean(labelError)}
                aria-describedby={labelError ? 'contact-label-error' : undefined}
                onChange={(e) => {
                  setNewLabel(e.target.value);
                  setLabelError('');
                }}
              />
              <button type="submit" className="primary" disabled={!newLabel.trim() || labelBusy}>
                <Plus size={15} /> {t('Créer')}
              </button>
            </form>
            {labelError && (
              <p id="contact-label-error" className="contact-error" role="alert">
                {labelError}
              </p>
            )}
            {labels.length === 0 ? (
              <p className="muted">{t('Aucun libellé pour le moment.')}</p>
            ) : (
              <ul className="label-list">
                {labels.map((l) => (
                  <li key={l.id}>
                    <Tag size={14} />
                    <span>
                      {l.name} <small>({l.count})</small>
                    </span>
                    <IconButton
                      label={t('Supprimer le libellé {name}', { name: l.name })}
                      onClick={() => void deleteLabel(l)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted">{t('Supprimer un libellé ne supprime aucun contact.')}</p>
          </div>
        </Modal>
      )}
      {purgeConfirm && (
        <Modal title={t('Vider la corbeille')} onClose={() => setPurgeConfirm(false)}>
          <p className="modal-body">
            {t('Les contacts de la corbeille seront supprimés définitivement.')}
          </p>
          <div className="modal-actions">
            <button className="text-button" onClick={() => setPurgeConfirm(false)}>
              {t('Annuler')}
            </button>
            <button className="danger" onClick={() => void emptyBin()}>
              {t('Vider la corbeille')}
            </button>
          </div>
        </Modal>
      )}
      {bulkConfirm && (
        <Modal
          title={t('Déplacer la sélection dans la corbeille')}
          onClose={() => setBulkConfirm(false)}
        >
          <div className="stack-form">
            <p className="modal-body">
              {t(
                '{n} contact(s) seront déplacés dans la corbeille. Vous pourrez les restaurer pendant 30 jours. Vos messages échangés seront conservés.',
                {
                  n: selected.size,
                },
              )}
            </p>
            <div className="modal-actions">
              <button className="text-button" onClick={() => setBulkConfirm(false)}>
                {t('Annuler')}
              </button>
              <button className="danger" onClick={() => void bulkDelete()}>
                {t('Déplacer dans la corbeille')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {draft && (
        <Modal
          title={draft.id ? t('Modifier le contact') : t('Nouveau contact')}
          onClose={() => setDraft(undefined)}
        >
          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            {error && <Notice type="error">{error}</Notice>}
            <label htmlFor="contact-name">{t('Nom')}</label>
            <input
              id="contact-name"
              value={draft.name}
              maxLength={200}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <label htmlFor="contact-email">{t('Adresse email')}</label>
            <input
              id="contact-email"
              type="email"
              required
              value={draft.email}
              maxLength={254}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
            <label htmlFor="contact-others">{t('Autres adresses')}</label>
            <textarea
              ref={othersInput}
              id="contact-others"
              rows={2}
              value={draft.others}
              aria-invalid={Boolean(othersError)}
              aria-describedby={
                othersError ? 'contact-others-help contact-others-error' : 'contact-others-help'
              }
              onChange={(e) => {
                setDraft({ ...draft, others: e.target.value });
                setOthersError('');
              }}
            />
            <small id="contact-others-help" className="contact-help">
              {t('Une adresse par ligne, dix au maximum.')}
            </small>
            {othersError && (
              <p id="contact-others-error" className="contact-error" role="alert">
                {othersError}
              </p>
            )}
            <label htmlFor="contact-company">{t('Société')}</label>
            <input
              id="contact-company"
              value={draft.company}
              maxLength={200}
              onChange={(e) => setDraft({ ...draft, company: e.target.value })}
            />
            <label htmlFor="contact-phone">{t('Téléphone')}</label>
            <input
              id="contact-phone"
              value={draft.phone}
              maxLength={40}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
            <label htmlFor="contact-notes">{t('Notes')}</label>
            <textarea
              id="contact-notes"
              rows={3}
              value={draft.notes}
              maxLength={2000}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
            <div className="modal-actions">
              <button type="button" className="text-button" onClick={() => setDraft(undefined)}>
                {t('Annuler')}
              </button>
              <button type="submit" className="primary" disabled={saving}>
                {saving ? t('Enregistrement…') : t('Enregistrer')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {confirm && (
        <Modal
          title={t('Déplacer ce contact dans la corbeille')}
          onClose={() => setConfirm(undefined)}
        >
          <div className="stack-form">
            <p className="modal-body">
              {t(
                '{name} sera déplacé dans la corbeille. Vous pourrez le restaurer pendant 30 jours. Vos messages échangés seront conservés.',
                {
                  name: confirm.name || confirm.email,
                },
              )}
            </p>
            <div className="modal-actions">
              <button className="text-button" onClick={() => setConfirm(undefined)}>
                {t('Annuler')}
              </button>
              <button className="danger" onClick={() => void remove(confirm)}>
                {t('Déplacer dans la corbeille')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {purgeTarget && (
        <Modal
          title={t('Supprimer définitivement ce contact')}
          initialFocus={purgeCancel}
          onClose={() => {
            if (!purging) setPurgeTarget(undefined);
          }}
        >
          <div className="stack-form">
            <p className="modal-body">
              {t('{name} sera supprimé définitivement. Cette action ne pourra pas être annulée.', {
                name: purgeTarget.name || purgeTarget.email,
              })}
            </p>
            {purgeError && <Notice type="error">{purgeError}</Notice>}
            <div className="modal-actions">
              <button
                className="text-button"
                ref={purgeCancel}
                disabled={purging}
                onClick={() => setPurgeTarget(undefined)}
              >
                {t('Annuler')}
              </button>
              <button
                className="danger"
                disabled={purging}
                onClick={() => void purgeContact(purgeTarget)}
              >
                {purging ? t('Suppression…') : t('Supprimer définitivement')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
